import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import {
  LedgerAccount,
  LedgerAccountPatch,
} from '../../entities/accounting/account.entities';
import { LedgerLineItem } from '../../entities/accounting/entry.entities';
import { LedgerStatistics } from '../../entities/accounting/ledgerStatistics.entities';
import {
  CloseYearRequest,
  CreateAdjustingEntryRequest,
  CreateApplyPrepaidInputsRequest,
  CreateBuyAssetWithLoanRequest,
  CreateBuyPrepaidInputsRequest,
  CreateExpenseRequest,
  CreateIncomeRequest,
  CreateLoanPaymentRequest,
  CreatePersonalExpenseRequest,
  CreatePersonalIncomeRequest,
  CreateTransferRequest,
  SetOpeningBalancesRequest,
} from '../../entities/accounting/request.entities';
import { BaseApiService } from '../api/base-api.service';

@Injectable({
  providedIn: 'root',
})
export class AccountingTransactionService extends BaseApiService {
  postIncome(farmId: string, data: CreateIncomeRequest): Observable<void> {
    this.logger.log('AccountingTransactionService postIncome called with farmId: ' + farmId);
    return this.post<void>(`/farms/${farmId}/accounting/income`, data);
  }

  recordExpense(farmId: string, data: CreateExpenseRequest): Observable<void> {
    this.logger.log('AccountingTransactionService recordExpense called with farmId: ' + farmId);
    return this.post<void>(`/farms/${farmId}/accounting/expense`, data);
  }

  recordPersonalIncome(
    farmId: string,
    data: CreatePersonalIncomeRequest,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService recordPersonalIncome called with farmId: ' + farmId);
    return this.post<void>(`/farms/${farmId}/accounting/personal-income`, data);
  }

  recordPersonalExpense(
    farmId: string,
    data: CreatePersonalExpenseRequest,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService recordPersonalExpense called with farmId: ' + farmId);
    return this.post<void>(
      `/farms/${farmId}/accounting/personal-expense`,
      data,
    );
  }

  transfer(farmId: string, data: CreateTransferRequest): Observable<void> {
    this.logger.log('AccountingTransactionService transfer called with farmId: ' + farmId);
    return this.post<void>(`/farms/${farmId}/accounting/transfer`, data);
  }

  buyAssetWithLoan(
    farmId: string,
    data: CreateBuyAssetWithLoanRequest,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService buyAssetWithLoan called with farmId: ' + farmId);
    return this.post<void>(
      `/farms/${farmId}/accounting/buy-asset-with-loan`,
      data,
    );
  }

  loanPayment(
    farmId: string,
    data: CreateLoanPaymentRequest,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService loanPayment called with farmId: ' + farmId);
    return this.post<void>(`/farms/${farmId}/accounting/loan-payment`, data);
  }

  buyPrepaidInputs(
    farmId: string,
    data: CreateBuyPrepaidInputsRequest,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService buyPrepaidInputs called with farmId: ' + farmId);
    return this.post<void>(
      `/farms/${farmId}/accounting/buy-prepaid-inputs`,
      data,
    );
  }

  applyPrepaidInputs(
    farmId: string,
    data: CreateApplyPrepaidInputsRequest,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService applyPrepaidInputs called with farmId: ' + farmId);
    return this.post<void>(
      `/farms/${farmId}/accounting/apply-prepaid-inputs`,
      data,
    );
  }

  setOpeningBalances(
    farmId: string,
    data: SetOpeningBalancesRequest,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService setOpeningBalances called with farmId: ' + farmId);
    return this.post<void>(
      `/farms/${farmId}/accounting/opening-balances`,
      data,
    );
  }

  createAdjustingEntry(
    farmId: string,
    data: CreateAdjustingEntryRequest,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService createAdjustingEntry called with farmId: ' + farmId);
    return this.post<void>(`/farms/${farmId}/accounting/adjusting-entry`, data);
  }

  searchAccounts(farmId: string, q?: string, isActive?: boolean): Observable<LedgerAccount[]> {
    this.logger.log('AccountingTransactionService searchAccounts called with farmId: ' + farmId);
    let httpParams = new HttpParams();

    if (q) {
      httpParams = httpParams.set('q', q);
    }
    if (isActive !== undefined) {
      httpParams = httpParams.set('is_active', isActive.toString());
    }

    return this.get<LedgerAccount[]>(
      `/farms/${farmId}/accounting/accounts`,
      httpParams,
    );
  }

  getAccountById(farmId: string, accountId: string): Observable<LedgerAccount> {
    this.logger.log('AccountingTransactionService getAccountById called with farmId: ' + farmId + ', accountId: ' + accountId);
    return this.get<LedgerAccount>(
      `/farms/${farmId}/accounting/accounts/${accountId}`,
    );
  }

  patchAccount(
    farmId: string,
    accountId: string,
    data: LedgerAccountPatch,
  ): Observable<void> {
    this.logger.log('AccountingTransactionService patchAccount called with farmId: ' + farmId + ', accountId: ' + accountId);
    return this.patch<void>(
      `/farms/${farmId}/accounting/accounts/${accountId}`,
      data,
    );
  }

  getAccountActivity(
    farmId: string,
    accountId: string,
    startDate?: Date,
    endDate?: Date,
  ): Observable<LedgerLineItem[]> {
    this.logger.log('AccountingTransactionService getAccountActivity called with farmId: ' + farmId + ', accountId: ' + accountId);
    let httpParams = new HttpParams();

    if (startDate) {
      httpParams = httpParams.set('start_date', startDate.toISOString());
    }
    if (endDate) {
      httpParams = httpParams.set('end_date', endDate.toISOString());
    }

    return this.get<LedgerLineItem[]>(
      `/farms/${farmId}/accounting/accounts/${accountId}/activity`,
      httpParams,
    );
  }

  bootstrapAccounting(farmId: string): Observable<void> {
    this.logger.log('AccountingTransactionService bootstrapAccounting called with farmId: ' + farmId);
    return this.post<void>(`/farms/${farmId}/accounting/bootstrap`, {});
  }

  closeYearEnd(farmId: string, data: CloseYearRequest): Observable<void> {
    this.logger.log('AccountingTransactionService closeYearEnd called with farmId: ' + farmId);
    return this.post<void>(`/farms/${farmId}/accounting/year-end/close`, data);
  }

  getTransactionStatistics(
    farmId: string,
  ): Observable<LedgerStatistics> {
    this.logger.log('AccountingTransactionService getTransactionStatistics called with farmId: ' + farmId);
    return this.get<LedgerStatistics>(
      `/farms/${farmId}/accounting/statistics`,
    );
  }
}
